import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { FileText, Upload, X } from "lucide-react";
import { uploadMimeType } from "@/lib/upload-mime";

interface HouseDocument { name: string; path: string }

// Host-side manager for a property's House Guide documents (lease, appliance
// manuals, etc.). Uploads go straight to object storage via a presigned URL,
// then a finalize call gives the object a public-read ACL and appends it to the
// property's document list — members open them from their portal dashboard.
export function HouseGuideDocuments({ propertyId, documents }: { propertyId: number; documents: HouseDocument[] }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const propertyKey = ["/api/app/properties", propertyId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: propertyKey });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/house-documents/upload-url`, {});
      const { uploadUrl, objectPath } = await res.json();
      const put = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": uploadMimeType(file) } });
      if (!put.ok) throw new Error("Upload failed");
      await apiRequest("POST", `/api/app/properties/${propertyId}/house-documents`, { name: file.name, objectPath });
    },
    onSuccess: () => { toast({ title: "Document added", description: "Residents can now see this in their House Guide." }); invalidate(); },
    onError: (err) => toast({ title: "Upload failed", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (path: string) => apiRequest("DELETE", `/api/app/properties/${propertyId}/house-documents`, { path }),
    onSuccess: invalidate,
    onError: (err) => toast({ title: "Could not remove", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">House Guide Documents</label>
      <p className="text-xs text-gray-500 mb-2">Lease, appliance manuals, house manual — residents open these from their portal.</p>
      {documents.length > 0 && (
        <ul className="mb-2 space-y-1">
          {documents.map((d) => (
            <li key={d.path} className="flex items-center justify-between rounded-md border p-2 text-sm">
              <a href={d.path} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-2 text-brand hover:underline">
                <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{d.name}</span>
              </a>
              <button
                className="ml-2 shrink-0 rounded p-1 text-gray-400 hover:text-red-600"
                aria-label={`Remove ${d.name}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(d.path)}
                data-testid={`button-remove-house-doc`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        disabled={upload.isPending}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) upload.mutate(f);
        }}
      />
      <Button type="button" size="sm" variant="outline" disabled={upload.isPending} onClick={() => fileRef.current?.click()} data-testid="button-upload-house-doc">
        <Upload className="mr-1.5 h-4 w-4" /> {upload.isPending ? "Uploading…" : "Upload document"}
      </Button>
    </div>
  );
}
